export const MASK_MAX_LENGTH = 100;

const MAX_EXACT_COUNT = 255;

const CLASS_CHARACTERS = new Set(["#", "A", "X"]);

const classPatterns: Record<string, string> = {
	"#": "[0-9]",
	A: "[A-Za-z]",
	X: "[A-Za-z0-9]",
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const compileReferenceFormatMask = (mask: string): RegExp | null => {
	if (validateReferenceFormatMask(mask)) {
		return null;
	}
	let pattern = "^";
	let position = 0;
	while (position < mask.length) {
		const current = mask[position];
		if (current !== "[") {
			pattern += escapeRegExp(current);
			position++;
			continue;
		}
		position++;
		const classPattern = classPatterns[mask[position].toUpperCase()];
		position++;
		let quantifier: string;
		if (mask[position] === "+") {
			quantifier = "+";
			position++;
		} else {
			const countStart = position;
			while (position < mask.length && mask[position] >= "0" && mask[position] <= "9") {
				position++;
			}
			const count = Number(mask.slice(countStart, position));
			if (mask[position] === "+") {
				quantifier = `{${count},}`;
				position++;
			} else {
				quantifier = `{${count}}`;
			}
		}
		position++;
		pattern += classPattern + quantifier;
	}
	return new RegExp(`${pattern}$`, "i");
};

export interface MaskPreset {
	id: string;
	label: string;
	mask: string;
}

export const maskPresets: MaskPreset[] = [
	{ id: "digits", label: "Digits only", mask: "[#+]" },
	{ id: "lettersAndDigits", label: "Letters and digits", mask: "[X+]" },
	{ id: "date", label: "Date (MM-DD-YYYY)", mask: "[#2]-[#2]-[#4]" },
	{ id: "phone", label: "Phone number", mask: "([#3]) [#3]-[#4]" },
];

export const validateReferenceFormatMask = (mask: string): string | null => {
	if (!mask.trim()) {
		return "Format cannot be empty";
	}
	if (mask.length > MASK_MAX_LENGTH) {
		return `Format cannot be longer than ${MASK_MAX_LENGTH} characters`;
	}
	let position = 0;
	while (position < mask.length) {
		const current = mask[position];
		if (current === "]") {
			return `Unexpected "]" outside a group at position ${position + 1}`;
		}
		if (current !== "[") {
			position++;
			continue;
		}
		const groupStart = position;
		position++;
		if (position >= mask.length) {
			return `Unclosed group starting at position ${groupStart + 1}`;
		}
		if (!CLASS_CHARACTERS.has(mask[position].toUpperCase())) {
			return `Unknown character class "${mask[position]}" at position ${position + 1}; expected "#", "A", or "X"`;
		}
		position++;
		if (position >= mask.length) {
			return `Unclosed group starting at position ${groupStart + 1}`;
		}
		if (mask[position] === "+") {
			position++;
		} else {
			const countStart = position;
			while (position < mask.length && mask[position] >= "0" && mask[position] <= "9") {
				position++;
			}
			if (position === countStart) {
				return `Missing count at position ${position + 1}; expected a number or "+"`;
			}
			const countText = mask.slice(countStart, position);
			const count = Number(countText);
			if (count < 1 || count > MAX_EXACT_COUNT) {
				return `Count "${countText}" at position ${countStart + 1} must be between 1 and ${MAX_EXACT_COUNT}`;
			}
			if (mask[position] === "+") {
				position++;
			}
		}
		if (position >= mask.length) {
			return `Unclosed group starting at position ${groupStart + 1}`;
		}
		if (mask[position] !== "]") {
			return `Expected "]" at position ${position + 1} but found "${mask[position]}"`;
		}
		position++;
	}
	return null;
};